﻿/*****
    Author:	    CT Software Systems, LLC
    Last Modified: 260425
    
*/
'use strict';

import { bindSelect } from './ctssHtml.js';
import {
    hasImgsToUpload,
    uploadImgToCloudAsync,
    uploadToImgCtrl
} from './ctssImg.js';
import {
    allSettled,
    getPromise,
    savePromise
} from './ctssJQuery.js';
import { getUSStates } from './ctssUSStates.js'; 
import {
    isDataModified,
    missingRequiredData,
    setModified
} from './ctssVal.js';
import {
    getMemberDataAsync,
} from './snMembers.js';

import {
    ImgsFolder,
    appDebugMode,
    getAppToken,
    getTestFolderParams,
    setMenu
} from './snSite.js';
import {
    getPastorsDDLDataAsync,
    getAdminsDDLDataAsync
} from './snStaff.js';

const { useTestFolder, testFolderParam } = getTestFolderParams();


const token = getAppToken();

/* private functions */

const getChurchDataAsync = async () =>
    await getPromise({ url: `Churches/${token.CG}${testFolderParam}` });

const refreshChurchElements = (church) => {
    if (church.subscriptionExpires === null) {
        $('#lblSubscriptionExpires').text('Subscription Free');
    } else {
        let statusAction = church.billingSubscriptionParams.subscriptionStatus === 2 ? 'Ends' : 'Renews';
        $('#lblSubscriptionExpires').text(`Subscription ${statusAction}: ` +
            new Date(church.subscriptionExpires).toLocaleDateString());
    }
    $('#lblChurchCode').text(church.churchCode);
    if (church.logoName.length > 0) {
        $('#imgLogo').prop('src', church.logosBaseURL + church.logoName);
    }
    $('#editName').val(church.name);
    $('#editAdr1').val(church.adr1);
    $('#editAdr2').val(church.adr2);
    $('#editCity').val(church.city);
    $('#ddlStates').val(church.st);
    $('#editZip').val(church.zip);
    $('#ddlPastors').val(church.seniorPastorID);
    $('#ddlAdmins').val(church.administratorID);
    $('#editContactFirstName').val(church.contactFirstName);
    $('#editContactLastName').val(church.contactLastName);
    $('#editContactEmail').val(church.contactEmail);
    $('#editContactPhone').val(church.contactPhone);
    $('#hiddenLogoName').val(church.logoName);
}

const saveChurchAsync = async () => {

    if (missingRequiredData({
        dataItems: [
            { ctrlNm: '#editName', labelText: 'Name' }
        ]
    })) return;
    $('#divErrMsg').addClass('hidden');

    if (appDebugMode) {
        console.log('use test folder: ', useTestFolder);
    }

    if (hasImgsToUpload()) { // Save after uploading image
        await uploadImgToCloudAsync({
            hasStorageParams: true,
            imgsFolder: ImgsFolder.Logos,
            imgCtrl: '#imgLogo',
            imgName: $('#hiddenLogoName').val(),
            isDefaultImg: false,
            saveAsJPEG: false,
            url: 'Storage/Img',
            useTestFolder: useTestFolder,
            postOnload: async (logoName) => {
                $('#hiddenLogoName').val(logoName);
                await saveChurchDataAsync();
            }
        });
    } else { // Save without uploading image
        await saveChurchDataAsync();
    }
};

const saveChurchDataAsync = async () => {

    const adr1 = $('#editAdr1').val();
    const city = $('#editCity').val();
    const st = $('#ddlStates').val();
    const hasAdrChanged = (church.adr1 !== adr1 || church.city !== city || church.st !== st);

    await savePromise({
        url: 'Churches',
        data: {
            Name: $('#editName').val(),
            Adr1: adr1,
            Adr2: $('#editAdr2').val(),
            City: city,
            St: st,
            ZIP: $('#editZip').val(),
            Latitude: church.latitude,
            Longitude: church.longitude,
            HasAdrChanged: hasAdrChanged,
            LogoName: $('#hiddenLogoName').val(),
            SeniorPastorID: $('#ddlPastors').val(),
            AdministratorID: $('#ddlAdmins').val(),
            ContactFirstName: $('#editContactFirstName').val(),
            ContactLastName: $('#editContactLastName').val(),
            ContactEmail: $('#editContactEmail').val(),
            ContactPhone: $('#editContactPhone').val()
        }
    }).then(() => setModified(false));
}

/* public variables and functions */

let church;

const initAsync = async () => {
    window.onbeforeunload = (e) => {
        if (isDataModified() === true) {
            return "Your changes have not been saved...";
        }
    };

    setMenu();

    const [churchData, pastorsDDLData, adminsDDLData] = await allSettled({
        promises: [
            getChurchDataAsync(),
            getPastorsDDLDataAsync(),
            getAdminsDDLDataAsync()
        ]
    });
    if (churchData.status !== 'rejected') {
        church = churchData.value;
        refreshChurchElements(church)
    };
    if (pastorsDDLData.status !== 'rejected') {
        bindSelect({ id: 'ddlPastors', items: pastorsDDLData.value })
    };
    if (adminsDDLData.status !== 'rejected') {
        bindSelect({ id: 'ddlAdmins', items: adminsDDLData.value })
    };
    bindSelect({ id: 'ddlStates', items: getUSStates() });

    $('#editLogoImg').off('change').on('change', () => {
        uploadToImgCtrl({ inputFileCtrl: '#editLogoImg', imgCtrl: '#imgLogo' });
    });
    $('input[id!="editLogoInput"],select').on('change', () => setModified(true));
    $('#btnBilling').on('click', () => window.location.href = '../Billing');
    $('#btnSave').on('click', saveChurchAsync);
    $('#ddlAdmins').on('change', async () => {

        try {
            const member = await getMemberDataAsync({ id: $('#ddlAdmins').val() });

            const s = "Overwrite Contact Information with the Primary Administrator's Information?";
            $('#confirmPrompt').text(s);
            $('#confirmDlg').modal('show');
            $('#btnConfirm').off('click').on('click', () => {
                $('#editContactFirstName').val(member.firstName);
                $('#editContactLastName').val(member.lastName);
                $('#editContactEmail').val(member.email);
                $('#editContactPhone').val(member.phone);
                $('#confirmDlg').modal('hide');
            })
        }
        catch (error) { }
    });
};

const saveChurchLogoAsync = async (params = {}) => {
    const {
        cg = token.CG, 
        logoName } = params;
    if (!logoName) {
        throw new Error('snChurches.saveChurchLogoAsync: Missing logoName parameter');
    }
    await savePromise({
        url: 'Churches/Logo',
        data: {
            CG: cg,
            LogoName: logoName
        }
    });
}


export {
    saveChurchLogoAsync
};

window.snChurches = {
    initAsync
};